import {gsap} from "gsap";
import {BrickSnake2000Symbol, SymbolArgs} from "./BrickSnake2000Symbol";
import {ReelView} from "../../../core/reel/simplereel/SimpleReelModel";
import {BrickSnake2000} from "../../BrickSnake2000";
import {BrickSnake2000GameAssets} from "../../BrickSnake2000GameAssets";
import {SpineGSAPWrapper} from "../../animation/SpineGSAPWrapper";

export class BrickSnake2000WildSymbol extends BrickSnake2000Symbol {
    public multiplier:number;
    public multiplierText:PIXI.Sprite;
    private landAnimation:PIXI.spine.Spine;
    private skinName:string;
    private bodyPart:string;
    private reviveBlink:PIXI.spine.Spine | undefined;

    constructor(args:SymbolArgs) {
        super(args);
        this.view.removeChild(this.winAnimation);
        this.winAnimation = new PIXI.spine.Spine(BrickSnake2000.assetLoader.getSpineAsset(BrickSnake2000GameAssets.SYMBOL_WILD));
        this.view.addChildAt(this.winAnimation, 0);

        this.landAnimation =
            new PIXI.spine.Spine(BrickSnake2000.assetLoader.getSpineAsset(BrickSnake2000GameAssets.SYMBOL_WILD));
        this.view.addChild(this.landAnimation);

        this.multiplier = args.symbolSize;
        if (this.multiplier > 1) {
            this.multiplierText = new PIXI.Sprite(BrickSnake2000.assetLoader.getTextures(`X_Mult/X_Mult_${this.multiplier}`)[0]);
            this.view.addChild(this.multiplierText);
            this.multiplierText.position.set(-55, -55);
        }
    }

    public getWinAnimationBased():gsap.core.Timeline {
        return gsap.timeline()
                .add(() => {
                    if (this.multiplierText != undefined)
                        this.multiplierText.position.set(-24, -64);
                    this.winAnimation.alpha = 1;
                    this.mainSprite.position.set(20, -20);
                    this.moveToWinLayer();
                })
                .add(SpineGSAPWrapper.createTimeline(this.winAnimation, 0, "W_Win_EMPTY"))
                .add(() => {
                    this.mainSprite.position.set(0, 0);
                    this.winAnimation.state.setEmptyAnimation(0,0);
                    this.winAnimation.alpha = 0;
                    this.moveToNormalLayer();
                    if (this.multiplierText != undefined)
                        this.multiplierText.position.set(-55, -55);
                });
    }

    public resetState(): gsap.core.Timeline {
        return gsap.timeline().add(() => {
            this.winAnimation.state.setEmptyAnimation(0,0);
            this.winAnimation.alpha = 0;
            this.moveToNormalLayer();
            this.alpha = 1;
        })
    }

    public setRotation(rotation:number, cameFrom?:string):void {
        this.mainSprite.scale.x = 1;
        if (rotation == Math.PI && cameFrom != undefined) {
            this.mainSprite.scale.x = cameFrom == "left" ? 1 : -1;
        } else {
            this.mainSprite.rotation = rotation;
        }
    }

    public baseGameLandAnimation():gsap.core.Timeline {
        return gsap.timeline()
            .add(() => {
                this.landAnimation.alpha = 1;
                this.mainSprite.alpha = 0;
                this.moveToWinLayer();
            })
            .add(SpineGSAPWrapper.createTimeline(this.landAnimation, 0, "W_Land"))
            .add(() => {
                this.landAnimation.alpha = 0;
                this.mainSprite.alpha = 1;
                this.moveToNormalLayer();
            })
    }

    public getLandAnimation(): gsap.core.Timeline {
        return BrickSnake2000.model.isFreespins()
               ? gsap.timeline()
               : this.baseGameLandAnimation();
    }

    public setSnakeSkin(skin:string):void {
        this.skinName = skin;
    }

    public getSnakeSkin():string {
        return this.skinName;
    }

    public setBodyPart(part:string):void {
        this.bodyPart = part;
    }

    public getBodyPart():string {
        return this.bodyPart;
    }

    public dieForReal():void {
        const previousRot = this.mainSprite.rotation;
        const previousScale = this.mainSprite.scale;

        this.view.removeChild(this.mainSprite);

        const textureName =
            BrickSnake2000GameAssets.getSymbolStateTexturePath(this.symbolName,
                                                               `${this.bodyPart}_Deder`);
        this.mainSprite = new PIXI.Sprite(BrickSnake2000.assetLoader.getTextures(textureName)[0]);
        this.mainSprite.anchor.set(0.5,0.5);
        this.mainSprite.scale.copyFrom(previousScale);
        this.mainSprite.rotation = previousRot;
        this.view.addChildAt(this.mainSprite, 0);
    }

    public die():void {
        const previousRot = this.mainSprite.rotation;
        const previousScale = this.mainSprite.scale;

        this.view.removeChild(this.mainSprite);

        const textureName =
            BrickSnake2000GameAssets.getSymbolStateTexturePath(this.symbolName,
                                                               `${this.bodyPart}_Ded${this.bodyPart == "_Head" ? "_2" : ""}`);
        this.mainSprite = new PIXI.Sprite(BrickSnake2000.assetLoader.getTextures(textureName)[0]);
        this.mainSprite.anchor.set(0.5,0.5);
        this.mainSprite.scale.copyFrom(previousScale);
        this.mainSprite.rotation = previousRot;
        this.view.addChildAt(this.mainSprite, 0);
    }

    public setWaiting():void {
        const previousRot = this.mainSprite.rotation;
        const previousScale = this.mainSprite.scale;

        this.view.removeChild(this.mainSprite);

        this.mainSprite = new PIXI.Sprite(BrickSnake2000.assetLoader.getTextures(BrickSnake2000GameAssets.getSymbolStateTexturePath(this.symbolName, `_Head${this.skinName}_Wait`))[0]);
        this.mainSprite.anchor.set(0.5,0.5);
        this.mainSprite.scale.copyFrom(previousScale);
        this.mainSprite.rotation = previousRot;
        this.view.addChildAt(this.mainSprite, 0);
    }

    public setEating():void {
        const previousRot = this.mainSprite.rotation;
        const previousScale = this.mainSprite.scale;

        this.view.removeChild(this.mainSprite);

        this.mainSprite = new PIXI.Sprite(BrickSnake2000.assetLoader.getTextures(BrickSnake2000GameAssets.getSymbolStateTexturePath(this.symbolName, `_Head${this.skinName}_Eat`))[0]);
        this.mainSprite.anchor.set(0.5,0.5);
        this.mainSprite.scale.copyFrom(previousScale);
        this.mainSprite.rotation = previousRot;
        this.view.addChildAt(this.mainSprite, 0);
    }

    public setNotEating():void {
        const previousRot = this.mainSprite.rotation;
        const previousScale = this.mainSprite.scale;

        this.view.removeChild(this.mainSprite);

        this.mainSprite = new PIXI.Sprite(BrickSnake2000.assetLoader.getTextures(BrickSnake2000GameAssets.getSymbolStateTexturePath(this.symbolName, `_Head${this.skinName}`))[0]);
        this.mainSprite.anchor.set(0.5,0.5);
        this.mainSprite.scale.copyFrom(previousScale);
        this.mainSprite.rotation = previousRot;
        this.view.addChildAt(this.mainSprite, 0);
    }

    public breakNeckStep1():void {
        const previousRot = this.mainSprite.rotation;
        const previousScale = this.mainSprite.scale;

        this.view.removeChild(this.mainSprite);

        this.mainSprite = new PIXI.Sprite(BrickSnake2000.assetLoader.getTextures(BrickSnake2000GameAssets.getSymbolStateTexturePath(this.symbolName, `_Head${this.skinName}_Eat_2`))[0]);
        this.mainSprite.anchor.set(0.5,0.5);
        this.mainSprite.scale.copyFrom(previousScale);
        this.mainSprite.rotation = previousRot;
        this.view.addChildAt(this.mainSprite, 0);
    }

    public breakNeckStep2():void {
        const previousRot = this.mainSprite.rotation;
        const previousScale = this.mainSprite.scale;

        this.view.removeChild(this.mainSprite);

        this.mainSprite = new PIXI.Sprite(BrickSnake2000.assetLoader.getTextures(BrickSnake2000GameAssets.getSymbolStateTexturePath(this.symbolName, `_Head${this.skinName}_Eat_3`))[0]);
        this.mainSprite.anchor.set(0.5,0.5);
        this.mainSprite.scale.copyFrom(previousScale);
        this.mainSprite.rotation = previousRot;
        this.view.addChildAt(this.mainSprite, 0);
    }

    public revive():gsap.core.Timeline {
        const previousRot = this.mainSprite.rotation;
        const previousScale = this.mainSprite.scale;

        this.reviveBlink = new PIXI.spine.Spine(BrickSnake2000.assetLoader.getSpineAsset(BrickSnake2000GameAssets.SNAKE_FX));
        this.reviveBlink.alpha = 0;
        this.reviveBlink.rotation = previousRot;
        this.reviveBlink.scale.copyFrom(previousScale);

        this.addChild(this.reviveBlink);

        return gsap.timeline()
            .add(() => {
                this.moveToWinLayer();
                this.reviveBlink!.alpha = 1;
            })
            .add(SpineGSAPWrapper.createTimeline(this.reviveBlink!, 0, "Resurrection"))
        .add(() => {
           this.reviveBlink!.state.setEmptyAnimation(0, 0) ;
           this.reviveBlink!.alpha = 0;
            this.moveToNormalLayer();
        })
    }
}
